/**
 * Форма ошибки бэкенда — `DomainExceptionFilter` (apps/backend):
 * `{ statusCode, code, message, meta? }`.
 * Валидация Nest (`ValidationPipe`) отдаёт `message` массивом строк.
 *
 * Живёт в пакете контракта, а не во фронтенде: разбор этой формы —
 * часть договорённости с бэкендом, и расхождение здесь это баг,
 * а не косметика. Потребителей два — мини-апп и админка.
 */
interface BackendErrorBody {
  statusCode?: number;
  code?: string;
  message?: string | string[];
  meta?: Record<string, unknown>;
}

/** Связь оборвалась: ответа от бэкенда не было вообще. */
export const NETWORK_ERROR_CODE = "NETWORK_ERROR";

/**
 * Запрос ушёл, но ответа не дождались.
 *
 * Отдельный код, а не общий `NETWORK_ERROR`: обрыв и таймаут требуют разного
 * совета пользователю — «проверь интернет» против «сервер думает, повтори».
 * Раньше их различал только текст, а по тексту ветвиться нельзя.
 */
export const NETWORK_TIMEOUT_CODE = "NETWORK_TIMEOUT";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly meta: Record<string, unknown> | undefined;

  /**
   * Сообщения по одному, как их прислал `ValidationPipe`.
   *
   * `message` склеивает их через «; » для показа одной строкой, но форме
   * с несколькими полями нужен список: иначе оператор видит простыню
   * вместо трёх понятных пунктов.
   */
  readonly messages: string[];

  constructor(
    status: number,
    code: string,
    message: string,
    meta?: Record<string, unknown>,
    messages?: string[],
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.meta = meta;
    this.messages = messages ?? [message];
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Конфликт домена: приём закрыт, не хватает Фанкоинов, Прогноз уже сделан. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** Обрыв или таймаут — ответа не было, состояние на сервере неизвестно. */
  get isNetwork(): boolean {
    return (
      this.code === NETWORK_ERROR_CODE || this.code === NETWORK_TIMEOUT_CODE
    );
  }

  static fromResponse(status: number, body: unknown): ApiError {
    const parsed = (body ?? {}) as BackendErrorBody;
    const message = Array.isArray(parsed.message)
      ? parsed.message.join("; ")
      : (parsed.message ?? `Запрос завершился со статусом ${String(status)}`);

    return new ApiError(
      status,
      parsed.code ?? `HTTP_${String(status)}`,
      message,
      parsed.meta,
      Array.isArray(parsed.message) ? parsed.message : [message],
    );
  }

  static network(cause: unknown): ApiError {
    const timedOut = cause instanceof DOMException && cause.name === "AbortError";
    return timedOut
      ? new ApiError(0, NETWORK_TIMEOUT_CODE, "Сервер не ответил вовремя")
      : new ApiError(0, NETWORK_ERROR_CODE, "Нет связи с сервером");
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Текст ошибки для пользователя: наружу не должно течь `[object Object]`. */
export function toUserMessage(error: unknown): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error) return error.message;
  return "Что-то пошло не так";
}
